import React, { useMemo, ComponentType } from 'react';
import isEqual from 'lodash/isEqual';
import BodyRow, { BodyRowPure } from './BodyRow';
import BodyDraggable from './BodyDraggable';
import BodyWrapper from './BodyWrapper';
import { IColumn, IRowData } from '../interfaces';

interface IProps {
  data: IRowData[],
  columns?: IColumn[],
  dataTest: string,
  emptyMessage?: string | React.ReactNode,
  emptyMessageClassName?: string,
  orderCallback?: (oldIndex: number, newIndex: number) => void,
  rowCallbacks?: {
    [key: string]: (event: Event, rowData: IRowData) => void,
  },
  rowClassNameCallback?: (rowData: IRowData) => string,
  isDraggable?: boolean,
  isUsePureUpdateComponent?: boolean,
  showEmptyStateMessage?: boolean,
  selectedRows?: IRowData[],
  isAnimation?: boolean,
  rowWrapper?: <P extends {}>(WrappedComponent: ComponentType<P>) => ComponentType<P & { selectedRows: IRowData[] }>,
  rows?: React.ReactNode,
  bodyComponent?: (columns: IColumn[]) => React.ReactNode,
  customCellProps?: { [key: string]: any },
  draggingClassName?: string,
  topNeighborClassName?: string,
  bottomNeighborClassName?: string,
  rowKey?: string | ((rowData: IRowData, index: number) => string),
  dataTestRow?: string | ((rowDat: IRowData, index: number) => string),
}

const Body = ({
  data,
  columns,
  dataTest,
  emptyMessage,
  emptyMessageClassName,
  orderCallback,
  rowCallbacks,
  rowClassNameCallback,
  isDraggable,
  isUsePureUpdateComponent,
  showEmptyStateMessage,
  selectedRows,
  isAnimation,
  rowWrapper = null,
  rows,
  bodyComponent = null,
  customCellProps,
  draggingClassName,
  topNeighborClassName,
  bottomNeighborClassName,
  rowKey = 'key',
  dataTestRow = null,
}: IProps) => {
  const BodyRowComponent = useMemo(() => {
    const Row = isUsePureUpdateComponent ? BodyRowPure : BodyRow;
    return rowWrapper ? rowWrapper(Row) : Row;
  }, []);

  if (data.length) {
    if (bodyComponent) {
      return <>{ bodyComponent(columns) }</>;
    }

    if (isDraggable || isAnimation) {
      return (
        <BodyDraggable
          data={ data }
          orderCallback={ orderCallback }
          isUsePureUpdateComponent={ isUsePureUpdateComponent }
          rowCallbacks={ rowCallbacks }
          rowClassNameCallback={ rowClassNameCallback }
          columns={ columns }
          selectedRows={ selectedRows }
          isAnimation={ isAnimation }
          rows={ rows }
          rowKey={ rowKey }
          draggingClassName={ draggingClassName }
          topNeighborClassName={ topNeighborClassName }
          bottomNeighborClassName={ bottomNeighborClassName }
        />
      );
    }

    return (
      <BodyWrapper>
        {
          rows || data.map((rowData, index) => {
            const key = (typeof rowKey === 'function' ? rowKey(rowData, index) : rowData[rowKey]) ?? index;

            const dataTest = (typeof dataTestRow === 'function' ? dataTestRow(rowData, index) : rowData[dataTestRow]) ?? null;

            return (
              <BodyRowComponent
                key={ key }
                rowIndex={ index }
                rowData={ rowData }
                rowCallbacks={ rowCallbacks }
                rowClassNameCallback={ rowClassNameCallback }
                columns={ columns }
                isSelected={ selectedRows?.some(isEqual.bind(this, rowData)) }
                selectedRows={ selectedRows }
                className={ rowData.className }
                customCellProps={ customCellProps }
                dataTest={ dataTest }
              />
            );
          })
        }
      </BodyWrapper>
    );
  }

  return (
    <BodyWrapper>
      <tr>
        <td
          colSpan={ columns.filter((column) => column.isVisible || column.isVisible === undefined).length }
          className={ emptyMessageClassName }
        >
          <div
            className={ emptyMessageClassName }
            data-test={ `${ dataTest }EmptyMessage` }
          >
            { showEmptyStateMessage ? (
              <>{ emptyMessage }</>
            ) : (
              <span style={ { visibility: 'hidden' } }>EMPTY</span>
            ) }
          </div>
        </td>
      </tr>
    </BodyWrapper>
  );
};

export default Body;
